import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { useAppTranslation } from '../../services/i18n';
import { dateToGui, todayYmd } from '../../helpers/date';
import { ReadOnlyField } from '../ReadOnlyField';
import { TextFormField } from '../form/TextFormField';
import { UserOptionSource, UserSelectFormField } from '../form/UserSelectFormField';
import { WorkplaceAssignmentKind } from '../../store/workplaces';
import { WorkplaceAssignment, WorkplaceLeadership } from '../../helpers/workplaceTree';
import { AssignmentValues, WorkplaceValues } from './WorkplaceDetail';

interface Props {
  kind: WorkplaceAssignmentKind;
  title: string;
  /** "Vedoucí" or "Finanční schvalovatel" -- the deputy is called the same in both cards */
  leaderLabel: string;
  values: AssignmentValues;
  /** the records behind the values; the only source of the people's names */
  held: WorkplaceLeadership;
  /** outside the form the card only reports who holds the posts */
  isEditable: boolean;
  isSaving: boolean;
}

/**
 * The picker holds a user_in_company_space id, which no endpoint can resolve into a person
 * on its own, so whoever already holds the post has to be named up front. Without it the
 * field comes up blank and, the value not being among the options, merely focusing and
 * leaving it would empty the post.
 */
const preselected = (assignments: WorkplaceAssignment[]): UserOptionSource[] | undefined =>
  assignments.length
    ? assignments.map((item) => ({
        user_in_company_space_id: item.user_in_company_space_id,
        full_name: item.full_name,
      }))
    : undefined;

/**
 * Read mode has no picker, so the names come straight off the assignments -- but they
 * are chipped the same way the picker chips them, so switching into the form does not
 * redraw the people into something else.
 */
const ReadOnlyPeople: React.FC<{
  label: string;
  assignments: WorkplaceAssignment[];
  isDeputy?: boolean;
  /** somebody stands in for the post today, so its holders are struck through here too */
  isReplaced?: boolean;
}> = ({ label, assignments, isDeputy, isReplaced }) => {
  const t = useAppTranslation();
  return (
    <ReadOnlyField
      label={label}
      value={
        <span className={'workplace-assignments__people'}>
          {assignments.length ? (
            assignments.map((item) => (
              <span
                key={item.id}
                className={
                  'workplace-assignments__person' +
                  (isDeputy ? ' workplace-assignments__person--deputy' : '') +
                  (isReplaced ? ' workplace-assignments__person--replaced' : '')
                }
              >
                {item.full_name}
              </span>
            ))
          ) : (
            <span className={'workplace-assignments__nobody'}>
              {t('workplaces.assignments.nobody')}
            </span>
          )}
          {isReplaced && !!assignments.length && (
            <span className={'workplace-assignments__replaced'}>
              {t('workplaces.assignments.replaced', { count: assignments.length })}
            </span>
          )}
        </span>
      }
    />
  );
};

export const WorkplaceAssignmentCard: React.FC<Props> = ({
  kind,
  title,
  leaderLabel,
  values,
  held,
  isEditable,
  isSaving,
}) => {
  const t = useAppTranslation();
  const { setFieldValue, submitCount, errors } = useFormikContext<WorkplaceValues>();
  // The shared date field never marks itself touched, so it keeps its complaint to itself
  // until a submit attempt -- and Save is inert exactly until then, which would leave the
  // user with a dead button and no reason. The card says it instead, up to that point.
  const complaints =
    submitCount === 0
      ? Object.values((errors.assignments?.[kind] || {}) as Record<string, string>)
      : [];
  const field = useCallback((name: string) => `assignments.${kind}.${name}`, [kind]);

  // the validity belongs to the deputy, so an empty post must not keep dates around.
  // Tied to the value rather than to the picker's callback: it then holds however the
  // post was vacated, and the shared field does not have to report it.
  const { deputy, validFrom, validTo } = values;
  useEffect(() => {
    if (deputy) {
      return;
    }
    if (validTo) {
      setFieldValue(field('validTo'), '');
    }
    if (validFrom !== todayYmd()) {
      setFieldValue(field('validFrom'), todayYmd());
    }
  }, [deputy, validFrom, validTo, field, setFieldValue]);

  return (
    <section className={'workplace-assignments__card workplace-assignments__card--' + kind}>
      <h4 className={'workplace-assignments__card-title'}>{title}</h4>
      <div className={'workplace-assignments__card-body'}>
        {isEditable ? (
          <>
            {/* the post takes several holders; the stand-in below is still just one */}
            <UserSelectFormField
              name={field('leaders')}
              label={leaderLabel}
              placeholder={t('workplaces.assignments.addPerson')}
              isMulti
              useCsId
              initialUsers={preselected(held.leaders)}
              shrink
              disabled={isSaving}
            />
            {/* the wrapper is only there to colour the picker's chip as a stand-in,
                the same as the read-only one below */}
            <div className={'workplace-assignments__deputy'}>
              <UserSelectFormField
                name={field('deputy')}
                label={t('workplaces.assignments.deputy')}
                placeholder={t('workplaces.assignments.addPerson')}
                isMulti={false}
                useCsId
                initialUsers={preselected(held.deputy ? [held.deputy] : [])}
                shrink
                disabled={isSaving}
              />
              {/* the picker takes no helper text of its own -- and the sentence matters:
                  the stand-in decides in the workflow, the post's holders keep their rights */}
              <p className={'workplace-assignments__hint'}>
                {t(
                  kind === 'budget'
                    ? 'workplaces.assignments.deputyHintBudget'
                    : 'workplaces.assignments.deputyHint'
                )}
              </p>
            </div>

            {/* the validity belongs to the stand-in, so with the post empty there is
                nothing for it to describe and the row stays out of the way */}
            {!!values.deputy && (
              <div className={'workplace-assignments__validity'}>
                <TextFormField
                  name={field('validFrom')}
                  label={t('workplaces.assignments.validFrom')}
                  type={'date'}
                  shrink
                  required
                  disabled={isSaving}
                  helperText={t('workplaces.assignments.validFromHint')}
                  // a start in the future would be accepted and then vanish: the API only
                  // ever returns assignments that are valid today
                  maxValue={todayYmd()}
                />
                <TextFormField
                  name={field('validTo')}
                  label={t('workplaces.assignments.validTo')}
                  type={'date'}
                  shrink
                  disabled={isSaving}
                  helperText={t('workplaces.assignments.validToHint')}
                  minValue={values.validFrom || undefined}
                />
              </div>
            )}
            {complaints.map((message) => (
              <p key={message} className={'workplace-assignments__error'}>
                {message}
              </p>
            ))}
          </>
        ) : (
          <>
            <ReadOnlyPeople
              label={leaderLabel}
              assignments={held.leaders}
              isReplaced={!!held.deputy}
            />
            <ReadOnlyPeople
              label={t('workplaces.assignments.deputy')}
              assignments={held.deputy ? [held.deputy] : []}
              isDeputy
            />
            {values.deputy && (
              <div className={'workplace-assignments__validity'}>
                <ReadOnlyField
                  label={t('workplaces.assignments.validFrom')}
                  value={dateToGui(values.validFrom)}
                />
                <ReadOnlyField
                  label={t('workplaces.assignments.validTo')}
                  value={dateToGui(values.validTo) || t('workplaces.assignments.openEnded')}
                />
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};
